import { ReactNode } from 'react';
import Tag, { TagProps, TagVariant } from 'src/components/primitives/Tag/Tag';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';

export type TagPreset =
	| 'new'
	| 'perp'
	| 'coming-soon'
	| 'zero-fees'
	| 'pre-launch'
	| 'leverage'
	| 'long'
	| 'short';

type PresetConfig = { content: ReactNode | null } & (
	| { variant: TagVariant; className?: never }
	| { variant?: never; className: string }
);

const PRESETS: Record<TagPreset, PresetConfig> = {
	new: { className: 'bg-star-yellow text-black', content: 'New' },
	perp: { variant: 'neutral', content: 'Perp' },
	'coming-soon': {
		className: 'bg-tooltip-bg text-text-default',
		content: 'Coming Soon',
	},
	'zero-fees': {
		className: 'bg-star-yellow-bg text-star-yellow',
		content: '0% Fees',
	},
	'pre-launch': { variant: 'subtle', content: 'Pre Launch' },
	leverage: { className: 'bg-purple-bg text-purple-link', content: null },
	long: {
		className: 'bg-positive-green-secondary-bg text-text-positive-green-button',
		content: 'Long',
	},
	short: {
		className: 'bg-negative-red-secondary-bg text-text-negative-red-button',
		content: 'Short',
	},
};

interface Props extends Omit<TagProps, 'className' | 'variant'>, WithClassName {
	preset: TagPreset;
}

/**
 * Provides fully-baked tags (color + content) for design-system-defined presets.
 * - Do NOT add a new preset to this component for every tag you come across.
 *   This is specifically for tags that have been defined in the design system.
 * - Do NOT try to override the styles defined here. Use the `Tag` primitive with
 *   a `variant` (semantic color) or `className` (one-off color) instead.
 */
export default function PresetTag({
	preset,
	className,
	children,
	...rest
}: Props) {
	const config = PRESETS[preset];

	return (
		<Tag
			{...rest}
			variant={config.variant}
			className={cj(config.className, className)}
		>
			{config.content ?? children}
		</Tag>
	);
}
